/**
 * AsicExecutor contract constants — deployed on Ethereum mainnet.
 *
 * The executor is the on-chain entry point for multi-route split swaps
 * produced by the water-filling optimizer. The frontend never calls it
 * directly; instead it POSTs to `/api/build-calldata` which returns the
 * pre-built calldata, and wagmi's `sendTransaction` dispatches the tx.
 * These constants are exported for:
 *
 *   - Setting the `to` field in the sendTransaction call
 *   - Parsing revert reasons from failed txs (wagmi can decode custom
 *     errors when given the ABI)
 *   - Watching for `Executed` events in the tx receipt
 *
 * Source: ASIC-Aggregator/contracts/src/AsicExecutor.sol
 * Audit:  PR #10 — 14 findings addressed, 13 unit tests + mainnet fork test
 *
 * @see https://etherscan.io/address/0xb4a77B928ECe9276aF1b349D3Ed1026042F7dFeE
 */

// =====================================================================
//                          Deployment info
// =====================================================================

/** Deployed AsicExecutor address on Ethereum mainnet. */
export const ASIC_EXECUTOR_ADDRESS =
  "0xb4a77B928ECe9276aF1b349D3Ed1026042F7dFeE" as const;

/** Creation transaction hash. */
export const ASIC_EXECUTOR_CREATION_TX =
  "0xdc71810577e699094f8794c992ac63e26eb4f9a09c8d7e214230ee9bbb42aaf1" as const;

/** Block number at deployment. */
export const ASIC_EXECUTOR_DEPLOY_BLOCK = 24_862_843;

/** Uniswap V4 PoolManager singleton (constructor arg). */
export const UNISWAP_V4_POOL_MANAGER =
  "0x000000000004444c5dc75cB358380D2e3dE08A90" as const;

// =====================================================================
//                               ABI
// =====================================================================

/**
 * Full ABI for AsicExecutor, generated from Foundry compilation output.
 * Includes the `execute` entry point, V3/V4 callbacks, the `Executed`
 * event, and all custom error types. Typed `as const` so wagmi/viem
 * can infer parameter and return types at compile time.
 */
export const ASIC_EXECUTOR_ABI = [
  {
    type: "constructor",
    inputs: [
      { name: "_poolManager", type: "address", internalType: "address" },
    ],
    stateMutability: "nonpayable",
  },
  {
    type: "function",
    name: "execute",
    inputs: [
      {
        name: "p",
        type: "tuple",
        internalType: "struct AsicExecutor.SwapParams",
        components: [
          { name: "tokenIn", type: "address", internalType: "address" },
          { name: "tokenOut", type: "address", internalType: "address" },
          { name: "minOutTotal", type: "uint256", internalType: "uint256" },
          { name: "recipient", type: "address", internalType: "address" },
          { name: "deadline", type: "uint256", internalType: "uint256" },
          {
            name: "routes",
            type: "tuple[]",
            internalType: "struct AsicExecutor.Route[]",
            components: [
              { name: "amountIn", type: "uint256", internalType: "uint256" },
              {
                name: "steps",
                type: "tuple[]",
                internalType: "struct AsicExecutor.Step[]",
                components: [
                  { name: "pool", type: "address", internalType: "address" },
                  { name: "kind", type: "uint8", internalType: "uint8" },
                  { name: "zeroForOne", type: "bool", internalType: "bool" },
                  { name: "tokenOut", type: "address", internalType: "address" },
                  { name: "v4Currency0", type: "address", internalType: "address" },
                  { name: "v4Currency1", type: "address", internalType: "address" },
                  { name: "v4Fee", type: "uint24", internalType: "uint24" },
                  { name: "v4TickSpacing", type: "int24", internalType: "int24" },
                  { name: "v4Hooks", type: "address", internalType: "address" },
                ],
              },
            ],
          },
        ],
      },
    ],
    outputs: [
      { name: "totalOut", type: "uint256", internalType: "uint256" },
    ],
    stateMutability: "nonpayable",
  },
  {
    type: "function",
    name: "uniswapV3SwapCallback",
    inputs: [
      { name: "amount0Delta", type: "int256", internalType: "int256" },
      { name: "amount1Delta", type: "int256", internalType: "int256" },
      { name: "", type: "bytes", internalType: "bytes" },
    ],
    outputs: [],
    stateMutability: "nonpayable",
  },
  {
    type: "function",
    name: "unlockCallback",
    inputs: [
      { name: "rawData", type: "bytes", internalType: "bytes" },
    ],
    outputs: [
      { name: "", type: "bytes", internalType: "bytes" },
    ],
    stateMutability: "nonpayable",
  },
  {
    type: "event",
    name: "Executed",
    inputs: [
      { name: "caller", type: "address", indexed: true, internalType: "address" },
      { name: "tokenIn", type: "address", indexed: true, internalType: "address" },
      { name: "tokenOut", type: "address", indexed: true, internalType: "address" },
      { name: "totalIn", type: "uint256", indexed: false, internalType: "uint256" },
      { name: "totalOut", type: "uint256", indexed: false, internalType: "uint256" },
      { name: "recipient", type: "address", indexed: false, internalType: "address" },
    ],
    anonymous: false,
  },
  { type: "error", name: "DeadlinePassed", inputs: [] },
  { type: "error", name: "EmptyRoute", inputs: [] },
  {
    type: "error",
    name: "InvalidV4TokenOut",
    inputs: [
      { name: "supplied", type: "address", internalType: "address" },
      { name: "expected", type: "address", internalType: "address" },
    ],
  },
  {
    type: "error",
    name: "MinOutNotMet",
    inputs: [
      { name: "got", type: "uint256", internalType: "uint256" },
      { name: "want", type: "uint256", internalType: "uint256" },
    ],
  },
  {
    type: "error",
    name: "PoolOverpay",
    inputs: [
      { name: "token", type: "address", internalType: "address" },
      { name: "maxPay", type: "uint256", internalType: "uint256" },
      { name: "actualPay", type: "uint256", internalType: "uint256" },
    ],
  },
  { type: "error", name: "Reentrant", inputs: [] },
  { type: "error", name: "SameTokenInOut", inputs: [] },
  {
    type: "error",
    name: "UnauthorizedCallback",
    inputs: [
      { name: "caller", type: "address", internalType: "address" },
      { name: "expected", type: "address", internalType: "address" },
    ],
  },
  {
    type: "error",
    name: "UnknownPoolKind",
    inputs: [{ name: "kind", type: "uint8", internalType: "uint8" }],
  },
  { type: "error", name: "V3SwapDeltaInvalid", inputs: [] },
] as const;

// =====================================================================
//                          ERC-20 subset
// =====================================================================

/**
 * Minimal ERC-20 ABI for allowance reads and approve writes.
 * Used by the approval flow before submitting a swap.
 */
export const ERC20_ABI = [
  {
    type: "function",
    name: "allowance",
    inputs: [
      { name: "owner", type: "address", internalType: "address" },
      { name: "spender", type: "address", internalType: "address" },
    ],
    outputs: [{ name: "", type: "uint256", internalType: "uint256" }],
    stateMutability: "view",
  },
  {
    type: "function",
    name: "approve",
    inputs: [
      { name: "spender", type: "address", internalType: "address" },
      { name: "amount", type: "uint256", internalType: "uint256" },
    ],
    outputs: [{ name: "", type: "bool", internalType: "bool" }],
    stateMutability: "nonpayable",
  },
] as const;
